import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, masterDataMap, ownPhysicalWarehouses, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingMasterData, WarehousingStockMovement, WarehousingStockUnit } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel, itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-stock-unit-view',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, DialogModule, ProgressBarModule,
    SelectModule, TableModule, TagModule, TextareaModule, ToastModule, PageDetailHeroComponent, PageFormSectionComponent,
    PageMetricStripComponent, PageShellComponent],
  providers: [MessageService],
  templateUrl: './warehousing-stock-unit-view.component.html'
})
export class WarehousingStockUnitViewComponent implements OnInit {
  readonly itemIdentityLabel = itemIdentityLabel;
  readonly itemSizeLabel = itemSizeLabel;
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  isLoading = false;
  unit?: WarehousingStockUnit;
  movements: WarehousingStockMovement[] = [];
  masterData = masterDataMap();
  returnDialogVisible = false;
  isReturning = false;
  returnForm = this.fb.group({
    regionId: [null as number | null, Validators.required],
    warehouseId: [null as number | null, Validators.required],
    zoneId: [null as number | null, Validators.required],
    conditionId: [null as number | null, Validators.required],
    notes: ['']
  });
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Stock Unit Details', 'On Hand Stock', '/warehousing/stock');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.loadUnit();
  }

  loadUnit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      stockUnits: this.warehousingService.getStockUnits(),
      movements: this.warehousingService.getStockUnitMovements(id),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.unit = (response.stockUnits.data || []).find(row => row.id === id);
        this.movements = response.movements.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = ownPhysicalWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.unit?.unitSku || this.unit?.unitCode || 'Stock Unit Details', 'On Hand Stock', '/warehousing/stock');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  metrics(): PageMetric[] {
    return [
      { label: 'On Hand', value: this.unit?.quantity || 0 },
      { label: 'Available', value: this.unit?.availableQuantity || 0, tone: 'success' },
      { label: 'Reserved', value: this.unit?.reservedQuantity || 0, tone: 'warn' },
      { label: 'Status', value: this.unit?.status || '-', tone: this.unit?.status === 'AVAILABLE' ? 'success' : 'info' }
    ];
  }

  movementLabel(row: WarehousingStockMovement): string {
    return [row.movementType, row.referenceType].filter(Boolean).join(' | ') || '-';
  }

  quantityTone(row: WarehousingStockMovement): 'success' | 'warn' | 'info' | 'secondary' {
    switch ((row.movementType || '').toUpperCase()) {
      case 'ENTRY':
      case 'RELEASE':
        return 'success';
      case 'RESERVE':
        return 'warn';
      case 'EXIT':
        return 'info';
      default:
        return 'secondary';
    }
  }

  movementRoute(row: WarehousingStockMovement): string {
    return [row.warehouseName, row.zoneName, row.conditionName].filter(Boolean).join(' / ') || '-';
  }

  quantityLabel(row: WarehousingStockMovement): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(row.quantity || 0);
  }

  statusFlow(row: WarehousingStockMovement): string {
    return [row.fromStatus, row.toStatus].filter(Boolean).join(' -> ') || '-';
  }

  statusSeverity = statusSeverity;

  canReturn(): boolean {
    return !!this.unit && (this.unit.quantity || 0) <= 0;
  }

  openReturnDialog(): void {
    if (!this.unit) return;
    this.returnForm.reset({
      regionId: this.unit.regionId,
      warehouseId: this.unit.warehouseId,
      zoneId: this.unit.zoneId,
      conditionId: this.unit.conditionId,
      notes: ''
    });
    this.returnDialogVisible = true;
  }

  submitReturn(): void {
    if (!this.unit || this.returnForm.invalid) {
      this.returnForm.markAllAsTouched();
      return;
    }
    const value = this.returnForm.getRawValue();
    const unitSku = this.unit.unitSku || this.unit.unitCode;
    if (!unitSku) return;
    this.isReturning = true;
    this.warehousingService.returnUnit({
      unitSku,
      regionId: value.regionId!,
      warehouseId: value.warehouseId!,
      zoneId: value.zoneId!,
      conditionId: value.conditionId!,
      notes: value.notes || undefined
    }).subscribe({
      next: () => {
        this.isReturning = false;
        this.returnDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Unit returned', detail: 'The unique unit SKU is available in the selected warehouse location.' });
        this.loadUnit();
      },
      error: error => {
        this.isReturning = false;
        this.messageService.add({ severity: 'error', summary: 'Unable to return unit', detail: error?.error?.message || 'The unit could not be returned to stock.' });
      }
    });
  }
}
